// The victimisation guard on a decision about a driver (clauses 31.14 and 27.8).
//
// For six months after a grievance, a warning, a dismissal or a referral to a
// hearing about the same man shows this banner. The decision maker ticks that
// he checked the decision is unrelated, and the tick is recorded on the
// grievance. The conduct case trigger (db/migrations/0723) refuses the decision
// without it, so this banner is the explanation, not the enforcement.
//
// It shows references only. A Driver Servant deciding a case may not be able to
// read the grievance, and does not need to: he needs to know it exists.
import { useCallback, useEffect, useState } from 'react'
import { ShieldAlert } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { useConfirmGrievanceGuard, useGetGrievanceGuard } from '../../hooks/backend/grievances'

type Guard = { driver_id: string | null; months: number; grievances: { ref: string; raised_at: string; status: string; checked: boolean; checked_by_me: boolean }[] }

export function GrievanceGuardBanner({ caseKind, caseId, driverId, onCleared }: {
  caseKind: 'conduct_case' | 'deduction' | 'shift_change' | 'dismissal' | 'other'
  caseId: string
  driverId?: string | null
  onCleared?: (cleared: boolean) => void
}) {
  const guard = useGetGrievanceGuard<Guard>()
  const confirm = useConfirmGrievanceGuard()
  const load = guard.trigger
  const [ticked, setTicked] = useState(false)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    void load({ driver_id: driverId ?? null, case_kind: caseKind, case_id: caseId }, { skipCache: true }).catch(() => undefined)
  }, [load, driverId, caseKind, caseId])
  useEffect(() => { refresh() }, [refresh])

  const rows = guard.data?.grievances ?? []
  const cleared = rows.every((g) => g.checked_by_me)
  useEffect(() => { onCleared?.(cleared) }, [cleared, onCleared])
  if (rows.length === 0) return null

  return (
    <div className="space-y-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-xs text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
      <p className="flex items-start gap-2 font-semibold">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        Open or recent grievance {rows.map((g) => g.ref).join(', ')}, check the decision is unrelated.
      </p>
      <p>
        Raising a grievance is never misconduct and never a ground for a decision against him (clauses 31.14 and 27.8). For {guard.data?.months ?? 6} months after a grievance, whoever decides a warning, a deduction, a shift change or a dismissal about him records that they checked.
      </p>
      {cleared ? (
        <p className="font-medium">You have recorded that you checked. It is on the grievance's timeline.</p>
      ) : (
        <>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
            <span>I have checked that this decision is unrelated to the grievance.</span>
          </label>
          <Input className="h-8 bg-background text-xs" value={note} onChange={(e) => setNote(e.target.value)} aria-label="How you checked"
            placeholder="How you know, for example: the events are from before the grievance" />
          {error && <p className="text-rose-800 dark:text-rose-200">{error}</p>}
          <Button size="sm" variant="outline" disabled={!ticked || confirm.loading}
            onClick={() => {
              setError(null)
              void confirm.trigger({ driver_id: driverId ?? null, case_kind: caseKind, case_id: caseId, note })
                .then(() => refresh())
                .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            }}>
            Record that I checked
          </Button>
        </>
      )}
    </div>
  )
}
